import { createPopper } from "@popperjs/core";
import React from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { BreakPoint } from "../models/screen";
import { getViewportBreakpoint } from "../utils/detectMobile";
import { focusElement } from "../utils/keyboardFocus";
import { IProps as IModalProps, Modal, ModalRef } from "./Modal";

export type PopperOptions = Parameters<typeof createPopper>[2];

interface IBaseProps {
    triggerContent: React.ReactNode;
    triggerBtnProps?: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
    >;
    popperOptions?: PopperOptions;
    modalProps?: Partial<IModalProps>;
    showArrow?: boolean;
    arrowCSSClass?: string;
    isStandardModalBelowSize?: BreakPoint;
    onAfterOpen?: () => void;
    onAfterClose?: () => void;
    disablePopper?: boolean;
    onParentSelector?: () => HTMLElement;
    children?: React.ReactNode;
}

interface IControlledProps extends IBaseProps {
    isOpen: boolean;
    onRequestOpen: () => void;
    onRequestClose: () => void;
    disableOpenButton?: boolean;
}

type IUncontrolledProps = IBaseProps;

const getPopperOptions = (
    popperOptions: PopperOptions | undefined,
    showArrow: boolean | undefined,
    arrowElem: React.RefObject<HTMLDivElement | null>,
): PopperOptions => {
    const options = {
        ...popperOptions,
        modifiers: [...(popperOptions?.modifiers || [])],
    };
    if (showArrow) {
        options.modifiers.push({
            name: "arrow",
            options: {
                element: arrowElem.current,
            },
        });
    }
    return options;
};

const PopoverTrigger = (props: {
    disableOpenButton: boolean | undefined;
    triggerBtnProps:
        | React.DetailedHTMLProps<
              React.HTMLAttributes<HTMLElement>,
              HTMLElement
          >
        | undefined;
    triggerContent: React.ReactNode;
    onRequestOpen: () => void;
    triggerDivElem: React.RefObject<HTMLDivElement | null>;
    triggerBtnElem: React.RefObject<HTMLButtonElement | null>;
}): React.ReactNode => {
    if (props.disableOpenButton) {
        return (
            <div {...props.triggerBtnProps} ref={props.triggerDivElem}>
                {props.triggerContent}
            </div>
        );
    }
    return (
        <button
            {...props.triggerBtnProps}
            onClick={props.onRequestOpen}
            ref={props.triggerBtnElem}
        >
            {props.triggerContent}
        </button>
    );
};

/**
 * Popover is a combination of:
 *
 * - a trigger button
 * - a modal positioned relative to the trigger button
 *
 * Can be used to create components like drop down menus, tooltip-like modals, etc.
 */
export const Popover = (props: IControlledProps): React.ReactNode => {
    const triggerBtnElem = React.useRef<HTMLButtonElement>(null);
    const triggerDivElem = React.useRef<HTMLDivElement>(null);
    const modalElem = React.useRef<ModalRef>(null);
    const arrowElem = React.useRef<HTMLDivElement>(null);
    const popperRef = React.useRef<ReturnType<typeof createPopper> | undefined>(
        undefined,
    );

    const triggerElem = props.disableOpenButton
        ? triggerDivElem
        : triggerBtnElem;

    const handleAfterOpen = (): void => {
        if (
            !triggerElem.current ||
            !modalElem.current ||
            !modalElem.current.contentElem.current
        ) {
            return;
        }
        if (popperRef.current) {
            popperRef.current.destroy();
        }
        // Should this be a normal non-positioned modal at the current screen size?
        const breakpoint = getViewportBreakpoint();
        if (
            !props.disablePopper &&
            (props.isStandardModalBelowSize === undefined ||
                breakpoint > props.isStandardModalBelowSize)
        ) {
            // Position the popover
            const popperOptions = getPopperOptions(
                props.popperOptions,
                props.showArrow,
                arrowElem,
            );
            popperRef.current = createPopper(
                triggerElem.current,
                modalElem.current.contentElem.current,
                popperOptions,
            );
        }

        // set variable for use in iframe
        const window = getWindow();
        const document = getDocument();
        if (window?.parentIFrame) {
            window.parentIFrame.getPageInfo(() => {
                const popoverClientHeight =
                    modalElem.current?.contentElem.current?.clientHeight.toString();
                document?.documentElement.style.setProperty(
                    "--basket-popover--height-popover-with-arrow",
                    popoverClientHeight ? popoverClientHeight : null,
                );
            });
        }

        // Bubble callback
        if (props.onAfterOpen) {
            props.onAfterOpen();
        }
    };

    const handleAfterClose = (): void => {
        if (triggerElem.current) {
            focusElement(triggerElem.current);
        }
        // Bubble callback
        if (props.onAfterClose) {
            props.onAfterClose();
        }
    };

    return (
        <>
            <PopoverTrigger
                disableOpenButton={props.disableOpenButton}
                triggerBtnProps={props.triggerBtnProps}
                triggerContent={props.triggerContent}
                onRequestOpen={props.onRequestOpen}
                triggerDivElem={triggerDivElem}
                triggerBtnElem={triggerBtnElem}
            />
            <Modal
                {...props.modalProps}
                ref={modalElem}
                isOpen={props.isOpen}
                onAfterOpen={handleAfterOpen}
                onAfterClose={handleAfterClose}
                onRequestClose={props.onRequestClose}
                shouldReturnFocusAfterClose={false}
                preCloseBtnContent={
                    props.showArrow && (
                        <div
                            className={`${props.arrowCSSClass} popper-arrow arrow`}
                            aria-hidden={true}
                            ref={arrowElem}
                        >
                            <div data-popper-arrow={true} />
                        </div>
                    )
                }
                parentSelector={props.onParentSelector}
            >
                {props.children}
            </Modal>
        </>
    );
};

export interface UncontrolledPopoverRef {
    requestClose: () => void;
}

type IUncontrolledPropsWithRef = IUncontrolledProps & {
    ref?: React.Ref<UncontrolledPopoverRef>;
};

/**
 * UncontrolledPopover is a wrapper around Popover that manages the
 * open/closed state for you. It's "uncontrolled" in React's "uncontrolled form
 * input" sense. I.E. the component user doesn't control the open state.
 * Rather, the component manages it internally.
 */
export const UncontrolledPopover = (
    props: IUncontrolledPropsWithRef,
): React.ReactNode => {
    const { ref, ...otherProps } = props;
    const [isOpen, setIsOpen] = React.useState(false);

    React.useImperativeHandle(ref, () => ({
        requestClose: () => {
            setIsOpen(false);
        },
    }));

    return (
        <Popover
            {...otherProps}
            isOpen={isOpen}
            onRequestOpen={() => {
                setIsOpen(true);
            }}
            onRequestClose={() => {
                setIsOpen(false);
            }}
        >
            {props.children}
        </Popover>
    );
};
